import { useEffect, useRef, useCallback } from "react";
import type { TocEntry } from "~/types/views";

/**
 * Sticky table-of-contents sidebar with scroll-spy.
 *
 * Extracts h2 headings from `.content h2` elements in the DOM and
 * highlights the current section as the user scrolls. Hidden on mobile;
 * visible on md+ breakpoints.
 */
export function TableOfContents({
  entries,
  heading = "Content",
}: {
  entries: TocEntry[];
  heading?: string;
}) {
  const navRef = useRef<HTMLElement>(null);

  const scrollSpy = useCallback(() => {
    if (!navRef.current) return;
    const headings = document.querySelectorAll(".content h2");
    const scrollPos = window.scrollY + window.innerHeight / 4;
    let activeId = "";
    headings.forEach((el) => {
      if ((el as HTMLElement).offsetTop <= scrollPos) {
        activeId = el.id;
      }
    });
    navRef.current.querySelectorAll("li").forEach((li) => {
      const link = li.querySelector("a");
      if (link?.getAttribute("href") === `#${activeId}`) {
        li.classList.add("active");
      } else {
        li.classList.remove("active");
      }
    });
  }, []);

  useEffect(() => {
    let throttled = false;
    const handler = () => {
      if (throttled) return;
      throttled = true;
      scrollSpy();
      setTimeout(() => {
        throttled = false;
      }, 100);
    };
    window.addEventListener("scroll", handler, { passive: true });
    scrollSpy();
    return () => window.removeEventListener("scroll", handler);
  }, [scrollSpy]);

  if (entries.length === 0) return null;

  return (
    <nav ref={navRef}>
      <div className="hidden font-body text-base font-bold text-charcoal md:ml-2 md:block">
        {heading}
      </div>
      <ul className="mt-1 hidden font-body text-sm leading-snug text-charcoal md:block">
        {entries.map((entry) => (
          <li
            key={entry.id}
            className="my-0 rounded-md px-2 py-1 text-gray-60 hover:bg-teal-dark/5 hover:text-charcoal [&.active]:font-semibold [&.active]:text-charcoal"
          >
            <a href={`#${entry.id}`}>{entry.text}</a>
          </li>
        ))}
      </ul>
    </nav>
  );
}
